import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import Select from "react-select";

import { useFormik } from "formik";
import * as Yup from "yup";

import { Vehicle } from "../../models/vehicle/vehicle";
import { Customer } from "models/customer/customer";
import { CreateVehicle, GetMakes, GetVehicleModels, UpdateVehicle } from "api/vehicle";
import { GetCustomers } from "api/customer";
import LoadingComponent from "common/components/loader";
import { GetJobs } from "api/job";
import { Job, JobAllocation, JobService } from "models/job/job";
import TableContainer from "common/table-container";
import { RoutePath } from "routes/route-path";
import { Invoice } from "models/invoice/invoice";
import { ViewInvoiceId } from "api/invoice";

const validationSchema = Yup.object({
  vehicleNo: Yup.string().required("Vehicle No is required"),
  year: Yup.string().required("Year is required"),
  //model: Yup.string().required("Model is required"),
  /*  ownerName: Yup.string().when("ownerType", {
    is: "new",
    then: Yup.string().required("Owner name is required"),
    otherwise: Yup.string().nullable(), // Allow null if existing
  }),
  ownerContactNo: Yup.string()
    .matches(/^[\d\s]*$/, "Phone number must contain only numbers and spaces")
    .when("ownerType", {
      is: "new",
      then: Yup.string().required("Contact number is required"),
      otherwise: Yup.string().nullable(), // Allow null if existing
    }),
  ownerEmail: Yup.string()
    .email("Please enter a valid email")
    .when("ownerType", {
      is: "new",
      then: Yup.string().required("Email is required"),
      otherwise: Yup.string().nullable(), // Allow null if existing
    }), */
});

interface Props {
  vehicle?: Vehicle;
  onVehicleSaveSuccess?: (vehicle: Vehicle) => void;
}

const AddEditVehicleComponent: React.FC<Props> = ({ vehicle, onVehicleSaveSuccess }) => {
  const navigate = useNavigate();
  // Retrieve query parameters
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const optParam = searchParams.get("opt");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle>();
  const [editMode, setEditMode] = useState(false);

  const [makes, setMakes] = useState<{ value: string; label: string }[]>([]);
  const [selectedMake, setSelectedMake] = useState<{ value: string; label: string }>();
  const [allModels, setAllModels] = useState<{ value: string; label: string; parent: string }[]>([]);
  const [filteredModels, setFilteredModels] = useState<{ value: string; label: string; parent: string }[]>([]);
  const [showModelName, setShowModelName] = useState(false);

  const [jobs, setJobs] = useState<Job[]>([]);

  const [ownerType, setOwnerType] = useState("new"); // 'existing' or 'new'
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();

  // Sample existing customers for autocomplete
  const [existingCustomers, setExistingCustomers] = useState<Customer[]>([]);
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 50 }, (_, i) => currentYear - i);
  };
  const years = generateYears();

  useEffect(() => {
    if (ownerType === "existing" && selectedCustomer) {
      formik.setFieldValue("ownerName", selectedCustomer.name);
      formik.setFieldValue("ownerContactNo", selectedCustomer.contactNumber);
      formik.setFieldValue("ownerEmail", selectedCustomer.email);
    } else if (ownerType === "new") {
      formik.setFieldValue("ownerName", "");
      formik.setFieldValue("ownerContactNo", "");
      formik.setFieldValue("ownerEmail", "");
    }
  }, [ownerType, selectedCustomer]);

  useEffect(() => {
    loadMakes();
    loadModels();
    loadCustomers();
  }, []);

  useEffect(() => {
    formik.setValues({
      vehicleNo: selectedVehicle?.vehicleNo ?? "",
      year: selectedVehicle?.year ?? "",
      chassisNo: selectedVehicle?.chassisNo ?? "",
      make: selectedVehicle ? makes.find((m) => m.value == (selectedVehicle.model?.manufacturer?.id ?? "")) : null,
      model: selectedVehicle ? makes.find((m) => m.value == (selectedVehicle.model?.id ?? "")) : null,
      modelName: selectedVehicle?.modelName ?? "",

      ownerName: selectedVehicle?.currentOwner?.name ?? "",
      ownerContactNo: selectedVehicle?.currentOwner?.contactNumber ?? "",
      ownerEmail: selectedVehicle?.currentOwner?.email ?? "",
      ownerType: selectedVehicle ? "existing" : "new", // Add ownerType to initial values
      referrerCode: selectedVehicle?.referrerCode ?? "", // Add referrerCode to initial values
    }); // Set formik values when prop changes
  }, [selectedVehicle]);
  useEffect(() => {
    if (vehicle) {
      setSelectedVehicle({ ...vehicle });
      setEditMode(true);
      formik.setValues(vehicle); // Set formik values when tenant prop changes
      formik.setFieldValue(
        "make",
        makes.find((m) => m.value == (vehicle.model?.manufacturer?.id ?? ""))
      );
      const _make = makes.find((m) => m.value == (vehicle.model?.manufacturer?.id ?? ""));
      setSelectedMake(_make);
      formik.setFieldValue(
        "model",
        filteredModels.find((m) => m.value == (vehicle.model?.id ?? ""))
      );

      setSelectedCustomer(vehicle.currentOwner);
      setOwnerType("existing");

      loadJobHistory(vehicle.vehicleNo ?? "");
    } else {
      setEditMode(false);
      setJobs([]);
    }
  }, [vehicle]);

  //TODO:open from existing vehicle from url param
  const formik: any = useFormik({
    initialValues: {
      vehicleNo: selectedVehicle?.vehicleNo ?? "",
      year: selectedVehicle?.year ?? "",
      chassisNo: selectedVehicle?.chassisNo ?? "",
      make: selectedVehicle ? makes.find((m) => m.value == (selectedVehicle.model?.manufacturer?.id ?? "")) : null,
      model: selectedVehicle ? makes.find((m) => m.value == (selectedVehicle.model?.id ?? "")) : null,
      modelName: selectedVehicle?.modelName ?? "",

      ownerName: selectedVehicle?.currentOwner?.name ?? "",
      ownerContactNo: selectedVehicle?.currentOwner?.contactNumber ?? "",
      ownerEmail: selectedVehicle?.currentOwner?.email ?? "",
      ownerType: selectedVehicle ? "existing" : "new", // Add ownerType to initial values
      referrerCode: selectedVehicle?.referrerCode ?? "", // Add referrerCode to initial values
    },
    validationSchema,
    onSubmit: (values: any) => {
      // console.log(values);
      addEditVehicle(values);
    },
  });

  const loadMakes = async () => {
    const res = await GetMakes();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({ value: (t.id ?? 0).toString(), label: t.name ?? "" }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setMakes([{ label: "Select", value: "" }, ..._st]);
    } else {
      setMakes([]);
    }
    if (selectedVehicle) {
      const _make = makes.find((m) => m.value == (selectedVehicle.model?.manufacturer?.id ?? ""));
      setSelectedMake(_make);
      formik.setFieldValue("make", _make);
    }
  };

  const loadModels = async () => {
    const res = await GetVehicleModels();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({
        value: (t.id ?? 0).toString(),
        label: t.name ?? "",
        parent: (t.manufacturer?.id ?? "").toString(),
      }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setAllModels(_st);
    } else {
      setAllModels([]);
    }
  };
  const loadCustomers = async () => {
    const res = await GetCustomers();
    if (!(res?.status?.isError ?? false) && res?.data) {
      setExistingCustomers(res?.data);
      if (selectedVehicle && selectedVehicle.currentOwner) {
        setSelectedCustomer(res?.data.find((c) => c.id == selectedVehicle.currentOwner!.id));
      }
    } else {
      setExistingCustomers([]);
    }
  };

  const handleResetClick = () => {
    setSelectedVehicle(undefined);
    setEditMode(false);
  };

  useEffect(() => {
    if (!selectedMake) {
      const _fm = [...allModels];
      _fm.push({ label: "Other", value: "-1", parent: "" });
      setFilteredModels(_fm);
    } else {
      const _fm = [...allModels.filter((s) => s.parent == selectedMake.value)];
      _fm.push({ label: "Other", value: "-1", parent: "" });
      setFilteredModels(_fm);
    }
  }, [selectedMake]);

  useEffect(() => {
    if (selectedVehicle) {
      formik.setFieldValue(
        "model",
        filteredModels.find((m) => m.value == (selectedVehicle.model?.id ?? ""))
      );
    }
  }, [filteredModels]);

  const addEditVehicle = async (values: any) => {
    console.log();
    try {
      setError("");
      setSuccess("");
      setLoading(true);
      console.log(formik.values);

      var _vehicle: Vehicle = {
        id: selectedVehicle?.id ?? "",
        vehicleNo: values.vehicleNo,
        year: values.year,
        chassisNo: values.chassisNo,
        currentOwner: {},
        referrerCode: values.referrerCode,
      };
      if (values.model && values.model.value != "-1") {
        _vehicle.model = {
          id: values.model.value,
          name: values.model.label,
        };
      } else {
        _vehicle.modelName = values.modelName;
        _vehicle.manufacturer = { id: values.make.value, name: values.make.label };
      }
      if (ownerType === "existing" && selectedCustomer) {
        _vehicle.currentOwner!.id = selectedCustomer.id ?? "";
      } else {
        _vehicle.currentOwner!.name = values.ownerName;
        _vehicle.currentOwner!.contactNumber = values.ownerContactNo;
        _vehicle.currentOwner!.email = values.ownerEmail;
      }
      const res = (_vehicle?.id ?? "") == "" ? await CreateVehicle(_vehicle) : await UpdateVehicle(_vehicle);

      if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
        setSuccess("success");
        console.log(res?.data);
        setSelectedVehicle(res?.data ?? {});
        if (optParam == "n") {
          navigate(`${RoutePath.Admin_Job_create}?v=` + res?.data?.id);
        }
      } else {
        setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
      }
      setLoading(false);
    } catch (error) {
      setError("Something went wrong...");
      setLoading(false);
    }
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
  };

  const loadJobHistory = async (vehicleNo: string) => {
    const res = await GetJobs(
      {
        vehicle: vehicleNo,
      },
      undefined,
      1000,
      1
    );
    if (!(res?.status?.isError ?? false) && res?.data) {
      setJobs(res?.data ?? []);
    }
  };
  const getJobStatusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "bg-yellow-500";
      case "APPROVAL_PENDING":
        return "bg-blue-500";
      case "APPROVED":
        return "bg-blue-700";
      case "ONGOING":
        return "bg-orange-500";
      case "COMPLETED":
        return "bg-green-500";
      case "CANCELLED":
        return "bg-gray-500";
      case "PAYMENT_PENDING":
        return "bg-purple-300";
      case "PAID":
        return "bg-purple-500";
      default:
        return "bg-gray-500"; // default color
    }
  };

  const onViewInvoice = async (invoiceId: string) => {
    const blob = await ViewInvoiceId(invoiceId ?? "0");

    if (blob && blob.size > 0) {
      // Create a URL for the Blob
      const url = URL.createObjectURL(blob);

      // Open the Blob URL in a new tab
      window.open(url, "_blank");

      //setSuccess("Invoice has been generated successfully");
    } else if (blob && blob.size == 0) {
      setError(`error: Invoice not found`);
    } else {
      setError(`error: in viewing invoice`);
    }
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
  };

  const columns = useMemo(
    () => [
      {
        header: "Job Summary",
        accessorKey: "",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const jobStatus = cell.row.original.jobStatusId ?? "";
          const jobStatusColor = getJobStatusColor(jobStatus);

          return (
            <>
              <div className="text-sm whitespace-normal">
                <p>{cell.row.original.vehicle.vehicleNo ?? ""}</p>
              </div>
              <p>{cell.row.original.description ?? ""}</p>

              <div className={`inline-block px-4 py-1 rounded-lg text-sm text-white font-semibold ${jobStatusColor}`}>
                <span>{jobStatus}</span>
              </div>
            </>
          );
        },
      },
      {
        header: "Invoice",
        accessorKey: "invoice",
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Format the totalGrossAmount to 2 decimal places with commas
          const formattedTotal = cell.row.original.totalAmount
            ? new Intl.NumberFormat().format(cell.row.original.totalAmount.toFixed(2))
            : "0.00"; // Fallback to "0.00" if no value exists

          return (
            <>
              <p>Total: {formattedTotal}</p>
              {(cell.row.original.invoices ?? [])
                .filter((i: Invoice) => !(i.isCancelled ?? false))
                .map((itm: Invoice, index: number) => (
                  <u
                    key={`inv_${index}`}
                    onClick={() => {
                      onViewInvoice(itm.id ?? "");
                    }}
                    className="cursor-pointer hover:text-blue-500"
                  >
                    {itm.invoiceReference ?? ""}
                  </u>
                ))}
            </>
          );
        },
      },
      {
        header: "Services",
        accessorKey: "services",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            {(cell.row.original.services ?? []).map((itm: JobService, index: number) => (
              <p key={`svc_${index}`}>{itm.service?.name ?? ""}</p>
            ))}
          </>
        ),
      },
      {
        header: "Assignment",
        accessorKey: "assignment",
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Create a Set to store unique names
          const uniqueNames = new Set<string>();

          // Loop through allocations to extract names
          (cell.row.original.allocations ?? []).forEach((itm: JobAllocation) => {
            const employee = itm.assignedEmployee;
            if (employee) {
              const fullName = `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim();
              if (fullName) {
                uniqueNames.add(fullName);
              }
            }
          });

          // Convert the Set back to an array
          const distinctNames = Array.from(uniqueNames);

          return (
            <>
              {(distinctNames ?? []).map((name: string, index: number) => (
                <p key={`asm_${index}`}>{name}</p>
              ))}
            </>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <form
        action="#!"
        id="vehicleForm"
        onSubmit={(event: any) => {
          event.preventDefault();
          formik.handleSubmit();
        }}
      >
        <div className="grid grid-cols-1 gap-x-5">
          <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-2">
            <div className="mb-4">
              <label htmlFor="vehicleNo" className="inline-block mb-2 text-base font-medium">
                Vehicle Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="vehicleNo"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Vehicle Number"
                value={formik.values.vehicleNo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.vehicleNo && formik.errors.vehicleNo ? (
                <p id="error-vehicleNo" className="mt-1 text-sm text-red-500">
                  {formik.errors.vehicleNo}
                </p>
              ) : null}
            </div>
            <div className="mb-4">
              <label htmlFor="chassisNo" className="inline-block mb-2 text-base font-medium">
                Chassis Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="chassisNo"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Chassis Number"
                value={formik.values.chassisNo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.chassisNo && formik.errors.chassisNo ? (
                <p id="error-chassisNo" className="mt-1 text-sm text-red-500">
                  {formik.errors.chassisNo}
                </p>
              ) : null}
            </div>

            <div className="mb-4">
              <label htmlFor="make" className="inline-block mb-2 text-base font-medium">
                Make <span className="text-red-500"></span>
              </label>
              <Select
                className="border-slate-200 focus:outline-none focus:border-custom-500"
                options={makes}
                isSearchable={false} // To disable search
                name="make"
                id="make"
                value={formik.values.make} // Set Formik's `serviceType` value
                onChange={(option) => {
                  setSelectedMake(option);
                  formik.setFieldValue("make", option);
                }} // Update Formik state
              />
              {formik.touched.make && formik.errors.make ? (
                <p id="error-make" className="mt-1 text-sm text-red-500">
                  {formik.errors.make.label || formik.errors.make}
                </p>
              ) : null}
            </div>
            <div className="mb-4">
              <label htmlFor="year" className="inline-block mb-2 text-base font-medium">
                Year <span className="text-red-500">*</span>
              </label>
              <select
                name="year"
                className="form-select"
                value={formik.values.year}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {formik.touched.year && formik.errors.year ? (
                <p className="mt-1 text-sm text-red-500">{formik.errors.year}</p>
              ) : null}
            </div>
            <div className="mb-4">
              <label htmlFor="model" className="inline-block mb-2 text-base font-medium">
                Model <span className="text-red-500"></span>
              </label>
              <Select
                className="border-slate-200 focus:outline-none focus:border-custom-500"
                options={filteredModels}
                isSearchable={false} // To disable search
                name="model"
                id="model"
                value={formik.values.model} // Set Formik's `serviceType` value
                onChange={(option) => {
                  formik.setFieldValue("model", option);
                  setShowModelName((option?.value ?? "") == "-1");
                }} // Update Formik state
              />
              {formik.touched.make && formik.errors.model ? (
                <p id="error-model" className="mt-1 text-sm text-red-500">
                  {formik.errors.model.label || formik.errors.model}
                </p>
              ) : null}
            </div>
            {showModelName && (
              <div className="mb-4">
                <label htmlFor="modelName" className="inline-block mb-2 text-base font-medium">
                  Model Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="modelName"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Model Name"
                  value={formik.values.modelName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.modelName && formik.errors.modelName ? (
                  <p id="error-modelName" className="mt-1 text-sm text-red-500">
                    {formik.errors.modelName}
                  </p>
                ) : null}
              </div>
            )}
            <div className="mb-4">
              <label htmlFor="referrerCode" className="inline-block mb-2 text-base font-medium">
                Referrer Code
              </label>
              <input
                type="text"
                name="referrerCode"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Referrer Code"
                value={formik.values.referrerCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.referrerCode && formik.errors.referrerCode ? (
                <p id="error-referrerCode" className="mt-1 text-sm text-red-500">
                  {formik.errors.referrerCode}
                </p>
              ) : null}
            </div>

            <div className="mb-4 xl:col-span-2">
              <h3 className="text-lg font-semibold mb-2">Customer Details</h3>
              {/* Separator */}
              <hr className="my-3" />
            </div>

            <div className="mb-4 xl:col-span-2">
              {/*   <label className="inline-block mb-2 text-base font-medium">Owner Type</label> */}
              <div className="flex items-center">
                <label className="mr-4">
                  <input
                    type="radio"
                    name="ownerType"
                    value="existing"
                    checked={ownerType === "existing"}
                    onChange={() => {
                      setOwnerType("existing");
                      setSelectedCustomer(undefined); // Reset selected customer
                    }}
                  />
                  <span className="px-2">Existing Customer</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="ownerType"
                    value="new"
                    checked={ownerType === "new"}
                    onChange={() => setOwnerType("new")}
                  />
                  <span className="px-2">New Customer</span>
                </label>
              </div>
            </div>

            {ownerType === "existing" && (
              <div className="mb-4">
                <label htmlFor="existingCustomer" className="inline-block mb-2 text-base font-medium">
                  Select Existing Customer
                </label>
                <input
                  type="text"
                  list="customerList"
                  placeholder="Start typing..."
                  value=""
                  onChange={(e) => {
                    const query = e.target.value;
                    if (query != "") {
                      const matchedCustomer = existingCustomers.find((customer) =>
                        (customer.name ?? "").toLowerCase().includes(query.toLowerCase())
                      );
                      setSelectedCustomer(matchedCustomer);
                    } else {
                      setSelectedCustomer(undefined);
                    }
                  }}
                  className="form-input"
                />
                <datalist id="customerList">
                  {existingCustomers.map((customer) => (
                    <option key={customer.id} value={customer.name} />
                  ))}
                </datalist>
                {selectedCustomer && (
                  <div className="mt-2">
                    <p>
                      <strong>Name:</strong> {selectedCustomer.name ?? ""}
                    </p>
                    <p>
                      <strong>Contact No:</strong> {selectedCustomer.contactNumber ?? ""}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedCustomer.email ?? ""}
                    </p>
                  </div>
                )}
              </div>
            )}

            {ownerType === "new" && (
              <>
                <div className="mb-4">
                  <label htmlFor="ownerName" className="inline-block mb-2 text-base font-medium">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="ownerName"
                    className="form-input"
                    placeholder="Customer Name"
                    value={formik.values.ownerName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.ownerName && formik.errors.ownerName ? (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.ownerName}</p>
                  ) : null}
                </div>

                <div className="mb-4">
                  <label htmlFor="ownerContactNo" className="inline-block mb-2 text-base font-medium">
                    Customer Contact No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="ownerContactNo"
                    className="form-input"
                    placeholder="Customer Contact Number"
                    value={formik.values.ownerContactNo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.ownerContactNo && formik.errors.ownerContactNo ? (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.ownerContactNo}</p>
                  ) : null}
                </div>

                <div className="mb-4">
                  <label htmlFor="ownerEmail" className="inline-block mb-2 text-base font-medium">
                    Customer Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="ownerEmail"
                    className="form-input"
                    placeholder="Customer Email"
                    value={formik.values.ownerEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.ownerEmail && formik.errors.ownerEmail ? (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.ownerEmail}</p>
                  ) : null}
                </div>
              </>
            )}

            <div className="mb-4"></div>
          </div>
          {editMode && (
            <div className="mb-4 xl:col-span-2">
              <h3 className="text-lg font-semibold my-4"> Job History</h3>
              {/* Separator */}
              <hr className="my-3" />
              <div>
                <div className="overflow-x-auto mt-2">
                  {jobs && jobs.length > 0 ? (
                    <TableContainer
                      isPagination={true}
                      columns={columns || []}
                      data={jobs || []}
                      totalCount={1000}
                      customPageSize={1000}
                      divclassName="overflow-x-auto"
                      tableclassName="w-full whitespace-nowrap"
                      theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                      thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                      tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                      PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                    />
                  ) : (
                    <div className="noresult">
                      <div className="py-6 text-center">
                        <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                        <p className="mb-0 text-slate-500 dark:text-zink-200">No job history found.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={handleResetClick}
              className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
            >
              <X className="inline-block size-4" /> <span className="align-middle">Reset</span>
            </button>
            <button
              type="submit"
              className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            >
              {(selectedVehicle?.id ?? "") != "" ? "Update" : "Add"} Vehicle
            </button>
            {(selectedVehicle?.id ?? "") != "" && (
              <button
                type="button"
                onClick={() => {
                  navigate(`${RoutePath.Admin_Job_create}?v=` + selectedVehicle?.id);
                }}
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                Create a Job
              </button>
            )}
          </div>
        </div>
      </form>
      {loading && <LoadingComponent />}
      {success && (
        <div
          className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
          id="successAlert"
        >
          <b>Success! </b>
          {<>Vehicle has been {!editMode ? "added" : "updated"} successfully</>}
        </div>
      )}
      {error && (
        <div
          className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
          id="successAlert"
        >
          <>
            Oops! Something went wrong while attempting to {!editMode ? "add" : "update"} vehicle. Please try again
            later or contact support for assistance.
          </>
        </div>
      )}
    </>
  );
};

export default AddEditVehicleComponent;
